import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bars3Icon, BeakerIcon, MoonIcon, PlusIcon, SunIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Criterion, EngineModelProvider, evaluationApi, Evaluation, EvaluationResult, settingsApi, Target } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { EditableCriteria } from '../components/evaluation/EditableCriteria';
import { EditableTargets } from '../components/evaluation/EditableTargets';
import { ResultTable } from '../components/evaluation/ResultTable';
import { editorPanelClass, inputClass, labelClass } from '../components/ui/formClasses';

export function Workspace() {
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const { theme, setTheme } = useThemeStore();
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [criteria, setCriteria] = useState<Criterion[]>([]);
  const [targets, setTargets] = useState<Target[]>([]);
  const [results, setResults] = useState<EvaluationResult[]>([]);
  const [engines, setEngines] = useState([
    { provider: 'Codex', model: 'gpt-4.1' },
    { provider: 'Claude', model: 'claude-sonnet-5' },
    { provider: 'Gemini', model: 'gemini-2.5-flash' },
  ]);
  const [engineModels, setEngineModels] = useState<Record<string, EngineModelProvider>>({});
  const [loadingModels, setLoadingModels] = useState(false);
  const [selectedEngines, setSelectedEngines] = useState<string[]>(['Codex']);
  const [aggregate, setAggregate] = useState(false);
  const [loadingRun, setLoadingRun] = useState(false);
  const [creatingEvaluation, setCreatingEvaluation] = useState(false);
  const [draggedEvaluationId, setDraggedEvaluationId] = useState<string>('');
  const [dragOverEvaluationId, setDragOverEvaluationId] = useState<string>('');
  const [workspaceError, setWorkspaceError] = useState('');
  const [runError, setRunError] = useState('');

  const selected = useMemo(() => evaluations.find((e) => e.id === selectedId), [evaluations, selectedId]);

  // Tracks the currently-selected evaluation for the async polling loop, so a poll
  // started for one evaluation stops updating state once the user switches away.
  const selectedIdRef = useRef(selectedId);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  const loadEvaluations = async () => {
    const response = await evaluationApi.list();
    setEvaluations(response.data);
  };

  const loadDetails = async () => {
    if (!selectedId) {
      setCriteria([]);
      setTargets([]);
      setResults([]);
      return;
    }
    const [c, t, r] = await Promise.all([evaluationApi.criteria(selectedId), evaluationApi.targets(selectedId), evaluationApi.results(selectedId)]);
    setCriteria(c.data);
    setTargets(t.data);
    setResults(r.data);
  };

  const loadEngineModels = async () => {
    setLoadingModels(true);
    try {
      const response = await evaluationApi.engineModels();
      const byProvider = response.data.reduce((acc: Record<string, EngineModelProvider>, item: EngineModelProvider) => {
        acc[item.provider] = item;
        return acc;
      }, {});
      setEngineModels(byProvider);
      setEngines((current) => current.map((engine) => {
        const providerModels = byProvider[engine.provider];
        if (!providerModels) return engine;
        const available = providerModels.models.map((model) => model.id);
        const nextModel = available.includes(engine.model) ? engine.model : providerModels.default_model;
        return { ...engine, model: nextModel };
      }));
    } catch (err: any) {
      // Keep the existing fallback dropdown values; evaluation-time errors remain visible.
    } finally {
      setLoadingModels(false);
    }
  };

  useEffect(() => {
    const boot = async () => {
      try {
        const response = await settingsApi.get();
        setTheme(response.data.theme);
      } catch {}
      await loadEvaluations();
    };
    boot();
  }, []);

  useEffect(() => {
    loadDetails();
    if (selectedId) loadEngineModels();
  }, [selectedId]);

  const createEvaluation = async () => {
    setWorkspaceError('');
    setCreatingEvaluation(true);
    try {
      const response = await evaluationApi.create({ name: 'New evaluation', product_type: 'Software platform', description: '' });
      await loadEvaluations();
      setSelectedId(response.data.id);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setWorkspaceError(detail.map((item: any) => item.msg).join(', '));
      } else if (typeof detail === 'string') {
        setWorkspaceError(detail);
      } else {
        setWorkspaceError(err.message || 'Could not create evaluation.');
      }
    } finally {
      setCreatingEvaluation(false);
    }
  };

  const saveEvaluation = async (patch: Partial<Evaluation>) => {
    if (!selected) return;
    await evaluationApi.update(selected.id, patch);
    await loadEvaluations();
  };

  const reorderEvaluations = async (sourceId: string, targetId: string) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    setWorkspaceError('');
    const current = [...evaluations];
    const sourceIndex = current.findIndex((evaluation) => evaluation.id === sourceId);
    const targetIndex = current.findIndex((evaluation) => evaluation.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const reordered = [...current];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setEvaluations(reordered);
    try {
      const response = await evaluationApi.reorder(reordered.map((evaluation) => evaluation.id));
      setEvaluations(response.data);
    } catch (err: any) {
      setEvaluations(current);
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setWorkspaceError(detail);
      } else {
        setWorkspaceError(err.message || 'Could not reorder evaluations.');
      }
      await loadEvaluations();
    }
  };

  const deleteEvaluation = async (evaluation: Evaluation) => {
    setWorkspaceError('');
    const confirmed = window.confirm(`Delete "${evaluation.name}"? This will also delete its criteria, targets, and results.`);
    if (!confirmed) return;
    try {
      await evaluationApi.remove(evaluation.id);
      if (selectedId === evaluation.id) {
        setSelectedId('');
        setCriteria([]);
        setTargets([]);
        setResults([]);
      }
      await loadEvaluations();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setWorkspaceError(detail);
      } else {
        setWorkspaceError(err.message || 'Could not delete evaluation.');
      }
    }
  };

  // Poll the results list until no result is still "running" (or a safety timeout).
  // Runs are processed in a background job on the server, so the initial /run call
  // returns immediately with placeholder rows; this surfaces them as they complete.
  const pollResults = async (evaluationId: string) => {
    const intervalMs = 3000;
    const deadline = Date.now() + 10 * 60 * 1000; // 10 min safety cap
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      if (selectedIdRef.current !== evaluationId) return; // user switched away
      let data: EvaluationResult[];
      try {
        data = (await evaluationApi.results(evaluationId)).data;
      } catch {
        continue; // transient error — keep polling
      }
      if (selectedIdRef.current !== evaluationId) return;
      setResults(data);
      if (!data.some((r) => r.status === 'running')) return; // all settled
    }
  };

  const run = async () => {
    if (!selected) return;
    const evaluationId = selected.id;
    setWorkspaceError('');
    setRunError('');
    setLoadingRun(true);
    try {
      const chosen = engines.filter((e) => selectedEngines.includes(e.provider));
      const started = await evaluationApi.run(evaluationId, chosen, aggregate);
      setResults(started.data); // show "running" placeholders immediately
      await pollResults(evaluationId);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setRunError(detail.map((item: any) => item.msg).join(', '));
      } else if (typeof detail === 'string') {
        setRunError(detail);
      } else {
        setRunError(err.message || 'Could not run evaluation.');
      }
    } finally {
      setLoadingRun(false);
    }
  };

  const toggleTheme = async () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    try { await settingsApi.update({ theme: next }); } catch {}
  };

  return (
    <div className="min-h-screen min-w-full bg-gray-50 dark:bg-gray-900">
      <header className="h-14 min-w-full bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-4">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Vendor Review</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">Signed in as {user?.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleTheme} className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{theme === 'dark' ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}</button>
          <button onClick={clearAuth} className="px-3 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-lg">Log out</button>
        </div>
      </header>

      <div className="flex">
        <aside className="w-80 min-h-[calc(100vh-3.5rem)] bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 p-4 space-y-4">
          <button disabled={creatingEvaluation} onClick={createEvaluation} className="w-full inline-flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg"><PlusIcon className="w-4 h-4 mr-1" /> {creatingEvaluation ? 'Creating...' : 'New evaluation'}</button>
          {workspaceError && <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">{workspaceError}</div>}
          <div className="space-y-2">
            {evaluations.map((evaluation) => (
              <div
                key={evaluation.id}
                draggable
                onDragStart={(event) => {
                  setDraggedEvaluationId(evaluation.id);
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('text/plain', evaluation.id);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  setDragOverEvaluationId(evaluation.id);
                }}
                onDragLeave={() => setDragOverEvaluationId((current) => current === evaluation.id ? '' : current)}
                onDrop={async (event) => {
                  event.preventDefault();
                  const sourceId = event.dataTransfer.getData('text/plain') || draggedEvaluationId;
                  setDraggedEvaluationId('');
                  setDragOverEvaluationId('');
                  await reorderEvaluations(sourceId, evaluation.id);
                }}
                onDragEnd={() => {
                  setDraggedEvaluationId('');
                  setDragOverEvaluationId('');
                }}
                className={`group flex items-start gap-2 rounded-lg border p-3 transition-colors cursor-move ${draggedEvaluationId === evaluation.id ? 'opacity-60' : ''} ${dragOverEvaluationId === evaluation.id && draggedEvaluationId !== evaluation.id ? 'ring-2 ring-green-400 ring-offset-2 ring-offset-white dark:ring-offset-gray-800' : ''} ${selectedId === evaluation.id ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
              >
                <Bars3Icon className="mt-1 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                <button onClick={() => setSelectedId(evaluation.id)} className="min-w-0 flex-1 text-left">
                  <div className="font-medium text-gray-900 dark:text-white truncate">{evaluation.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{evaluation.product_type || 'Evaluation'} · {new Date(evaluation.updated_at).toLocaleDateString()}</div>
                </button>
                <button title="Delete evaluation" aria-label={`Delete ${evaluation.name}`} onClick={(event) => { event.stopPropagation(); deleteEvaluation(evaluation); }} className="shrink-0 rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400">
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
            {evaluations.length === 0 && <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">Create an evaluation to begin.</div>}
          </div>
        </aside>

        <main className="flex-1 min-w-0 p-6 space-y-6">
          {!selected && (
            <div className="min-h-[calc(100vh-7rem)] flex items-center justify-center text-sm text-gray-400 dark:text-gray-600">
              Create a new evaluation to begin.
            </div>
          )}
          {selected && (
            <>
              <section className={editorPanelClass}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={labelClass}>Evaluation name</label>
                    <input className={inputClass} defaultValue={selected.name} onBlur={(e) => saveEvaluation({ name: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelClass}>Product type</label>
                    <input className={inputClass} defaultValue={selected.product_type || ''} onBlur={(e) => saveEvaluation({ product_type: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelClass}>Description</label>
                    <input className={inputClass} defaultValue={selected.description || ''} onBlur={(e) => saveEvaluation({ description: e.target.value })} />
                  </div>
                </div>
              </section>

              <section className={`${editorPanelClass} space-y-4`}>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Criteria</h2>
                <EditableCriteria evaluationId={selected.id} criteria={criteria} reload={loadDetails} evaluations={evaluations} />
              </section>

              <section className={`${editorPanelClass} space-y-4`}>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Targets</h2>
                <EditableTargets evaluationId={selected.id} targets={targets} reload={loadDetails} evaluations={evaluations} />
              </section>

              <section className={`${editorPanelClass} space-y-4`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Evaluation Engines</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <label title="Combine the selected engines into one impartial result. Scores are averaged across engines and the consensus write-up is produced by the neutral Groq-configured LLM." className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer select-none">
                      <input type="checkbox" checked={aggregate} onChange={(e) => setAggregate(e.target.checked)} />
                      Aggregate
                    </label>
                    <button disabled={loadingModels} onClick={loadEngineModels} className="inline-flex items-center px-3 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-60 text-gray-700 dark:text-gray-300 text-sm rounded-lg">{loadingModels ? 'Loading models...' : 'Refresh models'}</button>
                    <button disabled={loadingRun || selectedEngines.length === 0} onClick={run} className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg"><BeakerIcon className="w-4 h-4 mr-1" /> {loadingRun ? 'Running...' : aggregate ? 'Run aggregate' : 'Run evaluation'}</button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {engines.map((engine, index) => (
                    <div key={engine.provider} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                        <input type="checkbox" checked={selectedEngines.includes(engine.provider)} onChange={(e) => setSelectedEngines(e.target.checked ? [...selectedEngines, engine.provider] : selectedEngines.filter((p) => p !== engine.provider))} />
                        {engine.provider}
                      </label>
                      <select className={`${inputClass} mt-2`} value={engine.model} onChange={(e) => setEngines(engines.map((item, i) => i === index ? { ...item, model: e.target.value } : item))}>
                        {(engineModels[engine.provider]?.models?.length ? engineModels[engine.provider].models : [{ id: engine.model, label: engine.model }]).map((model) => (
                          <option key={model.id} value={model.id}>{model.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
                {runError && <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">{runError}</div>}
              </section>

              <section className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Results</h2>
                {results.map((result) => <ResultTable key={result.id} result={result} evaluationId={selected.id} reload={loadDetails} />)}
                {results.length === 0 && <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-10 text-center text-sm text-gray-500 dark:text-gray-400">Run an evaluation to generate a scored matrix and spreadsheet export.</div>}
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
